import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import { getUnitTypeMeta } from "@/features/organization/utils/organization.helpers";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import OrgBreadcrumb, {
  useOrgPathWithStatus,
} from "@/components/common/OrgBreadcrumb";
import { useDashboardFiltersStore } from "../hooks/useDashboardStore";
import { formatValue } from "../domains/formatting";
import { TARGET_ACCENT } from "../domains/theme";
import MetricStatNumbers from "./MetricStatNumbers";

const STATUS_LABEL = {
  parent_only_na: "Group level",
  qualitative_na: "Group level",
  has_remark_only: "n.a",
  mixed_unit: "Mixed units",
  undefined: "Undefined",
  missing_at_parent: "Missing at parent",
  missing: "Missing",
};

const SUMMARY_LABEL = {
  CUMULATIVE: "Total",
  LATEST: "Latest",
  AVERAGE: "Average",
};

const ConsolidatedMetricDetailRow = observer(
  ({ row, years, unit, temporalAggregation, subEntries }) => {
    const { t } = useTran();
    const { available } = useDashboardFiltersStore();
    const meta = getUnitTypeMeta(row.unitLevel);
    const Icon = meta.icon;
    const breadcrumb = (row.path || []).join(" > ");
    const statusLabel = STATUS_LABEL[row.status];

    const { pathWithStatus, orgStatus, orgInactiveDate } = useOrgPathWithStatus(
      row.orgId,
      available.orgTree,
    );

    const isStray = row.status === "missing_at_parent";
    const showValue = (v) => formatValue(v);
    const showActualValue = (v) => (isStray ? "n.a" : formatValue(v));

    return (
      <div
        className={cn(
          "rounded-md border border-border/60 bg-card/95 p-3",
          isStray && "opacity-60",
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border/80 bg-muted/40",
                meta.color,
              )}
            >
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-1.5">
                <p className="truncate text-sm font-semibold text-foreground">
                  {row.orgName}
                </p>
                <OrgStatusTag
                  status={orgStatus}
                  inactiveDate={orgInactiveDate}
                  className="text-[10px] shrink-0"
                />
              </div>
              <OrgBreadcrumb
                pathWithStatus={pathWithStatus}
                fallbackLabel={breadcrumb}
              />
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <MetricStatNumbers row={row} unit={unit} />
            {statusLabel && (
              <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {t(statusLabel)}
              </span>
            )}
          </div>
        </div>
        {subEntries?.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-[11px]">
              <thead>
                <tr className="text-muted-foreground">
                  {subEntries.map((s) => (
                    <th
                      key={s.key}
                      className="px-2 py-1 text-right font-medium"
                    >
                      {s.label}
                    </th>
                  ))}
                  <th className="border-l border-border/60 bg-muted/30 px-2 py-1 text-right font-medium text-emerald-600">
                    {t("Ratio")}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border/40">
                  {subEntries.map((s) => (
                    <td
                      key={s.key}
                      className="px-2 py-1 text-right tabular-nums text-foreground"
                    >
                      {showActualValue(row.subEntryValues?.[s.key])}
                    </td>
                  ))}
                  <td className="border-l border-border/60 bg-muted/20 px-2 py-1 text-right font-semibold tabular-nums text-emerald-600">
                    {showActualValue(row.actual)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          years?.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-[11px]">
                <thead>
                  <tr className="text-muted-foreground">
                    <th className="px-2 py-1 text-left font-medium">
                      {t("Year")}
                    </th>
                    {years.map((y) => (
                      <th key={y} className="px-2 py-1 text-right font-medium">
                        {y}
                      </th>
                    ))}
                    <th className="border-l border-border/60 bg-muted/30 px-2 py-1 text-right font-medium">
                      {t(SUMMARY_LABEL[temporalAggregation] || "Total")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-border/40">
                    <td
                      className="px-2 py-1 font-semibold"
                      style={{ color: TARGET_ACCENT }}
                    >
                      {t("Target")}
                    </td>
                    {years.map((y) => (
                      <td
                        key={y}
                        className="px-2 py-1 text-right tabular-nums text-foreground"
                      >
                        {showValue(row.perYear?.[y]?.target)}
                      </td>
                    ))}
                    <td
                      className="border-l border-border/60 bg-muted/20 px-2 py-1 text-right font-semibold tabular-nums"
                      style={{ color: TARGET_ACCENT }}
                    >
                      {showValue(row.target)}
                    </td>
                  </tr>
                  <tr className="border-t border-border/40">
                    <td className="px-2 py-1 font-semibold text-emerald-600">
                      {t("Actual")}
                    </td>
                    {years.map((y) => (
                      <td
                        key={y}
                        className="px-2 py-1 text-right tabular-nums text-foreground"
                      >
                        {showActualValue(row.perYear?.[y]?.actual)}
                      </td>
                    ))}
                    <td className="border-l border-border/60 bg-muted/20 px-2 py-1 text-right font-semibold tabular-nums text-emerald-600">
                      {showActualValue(row.actual)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    );
  },
);

export default ConsolidatedMetricDetailRow;
